/* Isolated visuals only. Shopify owns catalog, search, cart and checkout behavior. */
if (!customElements.get('yy-hardware')) {
  customElements.define('yy-hardware', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.hero = this.querySelector('.yy-hero');
      this.canvas = this.querySelector('.yy-neural');
      this.context = this.canvas?.getContext('2d');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.finePointer = matchMedia('(pointer: fine)');
      this.enabled = this.dataset.motion !== 'off';
      this.userPaused = false;
      this.inView = true;
      this.time = 0;
      this.lastFrame = 0;
      this.motionButton = this.querySelector('.yy-motion');
      this.overdriveButton = this.querySelector('.yy-overdrive');
      const panel = this.querySelector('.yy-motion-panel');
      if (panel && this.enabled) panel.hidden = false;
      this.motionButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.syncMotion();
      }, options);
      this.overdriveButton?.addEventListener('click', () => {
        this.dataset.intensity = this.dataset.intensity === 'overdrive' ? 'standard' : 'overdrive';
        this.syncIntensity();
      }, options);
      this.reduced.addEventListener('change', () => this.syncMotion(), options);
      document.addEventListener('visibilitychange', () => this.syncMotion(), options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries[0].isIntersecting;
          this.syncMotion();
        }, { rootMargin: '60px' });
        this.observer.observe(this.hero);
      }
      if ('ResizeObserver' in window) {
        this.resizer = new ResizeObserver(() => this.resize());
        this.resizer.observe(this.hero);
      } else {
        window.addEventListener('resize', () => this.resize(), options);
      }
      this.hero?.addEventListener('pointermove', event => {
        if (!this.running || !this.finePointer.matches) return;
        const rect = this.hero.getBoundingClientRect();
        this.pointerX = ((event.clientX - rect.left) / rect.width - .5) * -10;
        this.pointerY = ((event.clientY - rect.top) / rect.height - .5) * -8;
      }, options);
      this.hero?.addEventListener('pointerleave', () => { this.pointerX = 0; this.pointerY = 0; }, options);
      this.querySelectorAll('.yy-category').forEach(card => {
        card.addEventListener('pointermove', event => {
          if (this.reduced.matches || !this.finePointer.matches) return;
          const rect = card.getBoundingClientRect();
          card.style.setProperty('--yy-card-x', `${event.clientX - rect.left}px`);
          card.style.setProperty('--yy-card-y', `${event.clientY - rect.top}px`);
        }, options);
      });
      this.resize();
      this.syncIntensity();
      this.syncMotion();
    }
    syncIntensity() {
      const boost = this.dataset.intensity === 'overdrive';
      this.overdriveButton?.setAttribute('aria-pressed', String(boost));
      const label = this.querySelector('[data-yy-intensity-label]');
      if (label) label.textContent = boost ? 'Overdrive' : 'Standard';
      this.overdriveButton?.setAttribute('aria-label', boost ? 'Overdrive visual mode on. Switch to standard.' : 'Standard visual mode. Switch to overdrive.');
    }
    syncMotion() {
      const paused = this.userPaused || this.reduced.matches;
      this.dataset.motion = !this.enabled ? 'off' : paused ? 'paused' : 'on';
      this.dataset.visible = String(this.inView && !document.hidden);
      this.motionButton?.setAttribute('aria-pressed', String(paused));
      const label = this.querySelector('[data-yy-motion-label]');
      if (label) label.textContent = paused ? 'Resume animation' : 'Pause animation';
      this.running = this.enabled && !paused && this.inView && !document.hidden;
      cancelAnimationFrame(this.frame);
      this.lastFrame = 0;
      if (this.running && this.context) this.frame = requestAnimationFrame(time => this.tick(time));
      else {
        this.hero?.style.removeProperty('--yy-px');
        this.hero?.style.removeProperty('--yy-py');
      }
    }
    resize() {
      if (!this.canvas || !this.context || !this.hero) return;
      const rect = this.hero.getBoundingClientRect();
      this.width = rect.width;
      this.height = rect.height;
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
      this.canvas.width = Math.round(rect.width * ratio);
      this.canvas.height = Math.round(rect.height * ratio);
      this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
      const count = this.width < 750 ? 24 : 42;
      const seed = i => { const n = Math.sin(i * 127.1 + 311.7) * 43758.5453; return n - Math.floor(n); };
      this.points = Array.from({ length: count }, (_, i) => ({
        x: .06 + seed(i + 1) * .9,
        y: .06 + seed(i + 98) * .88,
        phase: seed(i + 21) * Math.PI * 2
      }));
      this.draw();
    }
    tick(timestamp) {
      if (!this.running) return;
      this.frame = requestAnimationFrame(time => this.tick(time));
      if (this.lastFrame && timestamp - this.lastFrame < 1000 / 30) return;
      const delta = this.lastFrame ? Math.min(timestamp - this.lastFrame, 70) : 0;
      this.lastFrame = timestamp;
      this.time += delta / 1000 * (this.dataset.intensity === 'overdrive' ? 1.7 : .65);
      if (this.finePointer.matches && this.width >= 750) {
        this.hero.style.setProperty('--yy-px', `${this.pointerX || 0}px`);
        this.hero.style.setProperty('--yy-py', `${this.pointerY || 0}px`);
      }
      this.draw();
    }
    draw() {
      const ctx = this.context;
      if (!ctx || !this.points) return;
      ctx.clearRect(0, 0, this.width, this.height);
      if (!this.enabled || this.reduced.matches) return;
      const t = this.time;
      const points = this.points.map(p => ({x: p.x * this.width + Math.sin(t * .16 + p.phase) * 14, y: p.y * this.height + Math.cos(t * .14 + p.phase) * 18}));
      const reach = this.width < 750 ? 140 : 210;
      ctx.lineWidth = .7;
      points.forEach((p, i) => {
        for (let j = i + 1; j < points.length; j++) {
          const q = points[j];
          const distance = Math.hypot(p.x - q.x, p.y - q.y);
          if (distance > reach) continue;
          ctx.strokeStyle = `rgba(112,222,220,${(1 - distance / reach) * .28})`;
          ctx.beginPath();ctx.moveTo(p.x, p.y);ctx.lineTo(q.x, q.y);ctx.stroke();
          if ((i + j) % 5 === 0) {
            const progress = (t * .17 + i * .19) % 1;
            const x = p.x + (q.x - p.x) * progress;
            const y = p.y + (q.y - p.y) * progress;
            ctx.fillStyle = this.dataset.intensity === 'overdrive' && i % 3 === 0 ? '#ffaf72' : '#a9fff6';
            ctx.beginPath();ctx.arc(x, y, 1.7, 0, Math.PI * 2);ctx.fill();
          }
        }
        const alpha = .25 + (Math.sin(t + i) + 1) * .18;
        ctx.fillStyle = `rgba(141,245,237,${alpha})`;
        ctx.beginPath();ctx.arc(p.x, p.y, i % 4 === 0 ? 2 : 1.1, 0, Math.PI * 2);ctx.fill();
      });
    }
    disconnectedCallback() {
      this.running = false;
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.resizer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  });
}
